import { useState, useMemo, useEffect } from "react";

import { i18n } from "../../../translate/i18n";

import { Default } from "../../../components/Default/Default";
import { PageHeading } from "../../../components/StyledComponents/styleComponents.index";

import { Button } from "@aidron/aidron-ds";

import { 
  NewTableOptions, 
  OrderDetailsTable,
  SuccessModal,
  PrefixDescriptionCell,
  CancelModalButton,
  PrefixNameCell,
  SelectCell,
  LoadingContent
} from '../../../components/SharedComponents/sharedComponents.index';
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";

import { COLUMNS_IDENTIFIERS, COLUMNS_LABELS } from "../../../components/components.constants";

import { usePrefixes } from "../../../model/usePrefixOfMeasure";

const { t } = i18n;

const generatePatchPrefixesPayload = (prefixId) => {
  return [{ prefixId, isPrefixAvailable: false }];
};

const PrefixFormModal = ({ actions, uiState }) => {
  const { showFormModal, labels } = uiState || {};
  const { setShowFormModal, onSubmit } = actions || {};

  const [prefixName, setPrefixName] = useState("");
  const [prefixDescription, setPrefixDescription] = useState("");

  const {
    createPrefix,
    isCreating,
    isCreateError,
  } = usePrefixes();

  const isFormValid = prefixName.trim().length > 0 && prefixDescription.trim().length > 0;

  const handleSubmit = () => {
    if (!isFormValid) {
      alert("Preencha todos os campos");
      return;
    }

    createPrefix(
      { prefixName: prefixName.trim(), prefixDescription: prefixDescription.trim() },
      {
        onSuccess: (data) => {
          if (onSubmit) onSubmit(data);
          setPrefixName("");
          setPrefixDescription("");
          setShowFormModal(false);
        },
        onError: () => {
          setPrefixName("");
          setPrefixDescription("")
        },
      }
    );
  };

  const handleClose = () => {
    setPrefixName("");
    setPrefixDescription("");
    setShowFormModal(false);
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    backdropFilter: "blur(2px)",
  };

  const modalStyle = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    inlineSize: "80%",
    maxWidth: "500px",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "#333",
  };

  const hintStyle = {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px",
  };  

  if (!showFormModal) return null;

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "600" }}>
          {labels.title}
        </h2>

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Campo Sigla */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>{labels.firstInputTextFied}</label>
            <input
              type="text"
              value={prefixName}
              onChange={(e) => setPrefixName(e.target.value.slice(0, 10))}
              maxLength={10}
              style={inputStyle}
              placeholder="Ex: kg, m, un"
              disabled={isCreating}
            />
            <span style={hintStyle}>{labels.firstTextCheck}</span>
          </div>

          {/* Campo Descrição */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>{labels.secondInputTextFied}</label>
            <textarea
              value={prefixDescription}
              onChange={(e) => setPrefixDescription(e.target.value.slice(0, 255))}
              maxLength={255}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              placeholder="Descreva o prefixo de medida"
              disabled={isCreating}
            />
            <span style={hintStyle}>
              {prefixDescription.length}/{labels.secondTextCheck}
            </span>
          </div>

          {/* Botões */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "8px" }}>
            <Button onClick={handleClose} label={t("cancelEdit")} type="outlined" disabled={isCreating} />
            <Button onClick={handleSubmit} label={t("save")} type="filled" disabled={!isFormValid || isCreating} />
          </div>

          {/* Erro */}
          {isCreateError && (
            <div style={{ color: "red", fontSize: "13px", marginTop: "8px" }}>
              {"Erro ao salvar. Tente novamente."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function MeasurementUnits() {

  const [inputs, setInputs] = useState([]);
  const [showInputs, setShowInputs] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [checkedProducts, setCheckedProducts] = useState([]);

  const { 
    data: apiData, isLoading, 
    patchPrefixes, isUpdating 
  } = usePrefixes();

  const prefixesAvailable = apiData?.filter(item => {
    return item.isPrefixAvailable
  });

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: COLUMNS_IDENTIFIERS.PREFIX_DESCRIPTION,
        header: COLUMNS_LABELS.DESCRIPTION,
        cell: (props) => (
          <PrefixDescriptionCell
            {...props}
            showInputs={showInputs}
            inputs={inputs}
            setInputs={setInputs}
            prefixes={apiData}
            saveLoading={isUpdating}/>), 
        enableSorting: true
      },
      {
        accessorKey: COLUMNS_IDENTIFIERS.PREFIX_NAME,
        header: COLUMNS_LABELS.CODE,
        cell: (props) => (
          <PrefixNameCell
            {...props}
            showInputs={showInputs}
            inputs={inputs}
            setInputs={setInputs}
            prefixes={apiData}
            isPrefixOfMeasurePage={true}
            suppliersAndProductRelations={[]}
            saveLoading={isUpdating}/>),   
        enableSorting: true
      },      
    ];

    const selectionColumn = {
      header: "Selecione",
      id: "select-product",
      cell: ({ row }) => (
        <SelectCell
          ctx={{
            data: row.original,
            isPrefixOfMeasurePage: true
          }}
          actions={{
            setCheckedProducts: setCheckedProducts,
          }}
          uiState={{
            showInputs: showInputs,
            checkedProducts: checkedProducts,
          }}/>
      ),
      enableSorting: false
    };

    return showInputs ? [selectionColumn, ...baseColumns] : baseColumns;    
  }, [showInputs, apiData, inputs, checkedProducts])

  const onInputsSave = async () => {
    try {
      
      const result = await patchPrefixes({ 
        inputs: generatePatchPrefixesPayload(checkedProducts),
        hasToRemovePrefix: true
      });

      setShowInputs(false);
      setCheckedProducts([]);

      if (!result) return;

      setShowSuccessModal(true);
        
    } catch (error) {
      throw error;
    }
  };

  const onSubmit = async () => {};

  if (isLoading) return <LoadingContent label={"Carregando siglas..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>

  return (
    <Default >
      <div 
        style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "0.5fr",
        paddingBlockStart: "5rem",
        gap: "1rem",
        inlineSize: "100%",
        padding: "2rem 0 0 1rem"}}>

        <PageHeading>{t("purchaseMeasurementUnitParameters")}</PageHeading>

        <NewTableOptions 
          actions={{
            setSelectedProductIds: () => {},
            setShowInputs: setShowInputs,
            setShowCancelModal: setShowCancelModal,
            onInputsSave: onInputsSave,
            setInputs: setInputs,
            setShowFormModal: setShowFormModal
          }}
          ctx={{
            selectedHierarchyIndexes: [],
            productIds: [],
            selectedProductIds: [],
            inputs: inputs
          }}
          uiState={{
            showFilterButton: false,
            showInputs: showInputs,
            freezeColumns: false,
            downloadButton: false,
            hierarchyColumnsButton: false,
            actionButtons: true,            
            showCustomHeading: true,
            isPrefixOfMeasurePage: true,
            labels: {
              titlePage: t("unitsOfMeasure")
            }      
          }}/>

        <OrderDetailsTable 
          ctx={{
            columns: columns,
            tableSourceData: prefixesAvailable,
            tableSourceMetaData: {
              enableSorting: true
            }            
          }}
          uiState={{
            hasWhiteBackground: true,
            cellStyle: { textAlign: 'left' },
            headerStyle: { textAlign: 'left' }
          }}/>

          {showCancelModal && (
            <CancelModalButton
              t={t}
              setShowInputs={setShowInputs}
              setInputs={setInputs}
              setShowCancelModal={setShowCancelModal}/>
          )}

          {showSuccessModal && (
            <SuccessModal
              actions={{ setShowSuccessModal: setShowSuccessModal }}
              ctx={{ t: t }}
              uiState={{ isPrefixOfMeasurePage: true }}
              />
          )}

          {showFormModal && (
            <PrefixFormModal 
              actions={{
                setShowFormModal: setShowFormModal, 
                onSubmit: onSubmit
              }}
              uiState={{
                showFormModal: showFormModal,
                labels: {
                  title: t("addPrefixOfMeasureLabel"),
                  firstTextCheck: t("maxTenCharacters"),
                  secondTextCheck: t("max255Characters"),
                  firstInputTextFied: t("code"),
                  secondInputTextFied: t("description")
                }
              }}/>
          )} 
      </div>
    </Default>
  );
}